import React from "react";
import Box from "@mui/material/Box";
import Columns from "../../../atoms/Columns";
import Column from "../../../atoms/Column";
import { Form, Props } from "../../../pages/Crew/Departure/Create/types";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CommonTextFIeld from "../../../atoms/CommonTextFIeld";
import CommonNumberFIeld from "../../../atoms/CommonNumberFIeld";
import { ShipDexie } from "../../../../dexie/Ship";
import { EUorUk, LorB, LorU } from "../../../../gen/graphql/types";
import Steppr from "../../../atoms/Stepper";
import RadioButtun from "../../../atoms/RadioButton";
import LastReportText from "../../../atoms/LastReportText";
import { ArrivalDexie } from "../../../../dexie/Arrival";
import CustomTooltip from "../../../atoms/CustomTooltip";
import UtcDateTimePicker from "../../../atoms/UtcDateTimePicker";
import FormBlock_Spare from "./FormBlock_Spare";
import CommonSelect from "../../../atoms/CommonSelect";
import RobAdditionalForDeparture from "./ROB/RobAdditionalForDeparture";
import { convertEmptyStringToNull } from "../../../../utils/formats";

type FormBlockProps = Props & {
  title: string;
  isPreview: boolean;
  onUpdateForm?: (value: Form) => void;
  ship?: ShipDexie;
  lastArrival?: ArrivalDexie;
  defaultTimeZone?: string;
  isConfirm: boolean;
  setIsConfirm: React.Dispatch<React.SetStateAction<boolean>>;
};

const FormBlock = (props: FormBlockProps): React.ReactElement => {
  const onChange = (
    key: string,
    value: string | number | Date | null | undefined
  ) => {
    if (typeof value === "string") {
      value = convertEmptyStringToNull(value);
    }

    const form = {
      ...props.form,
      [key]: value,
    };

    props.onUpdateForm?.(form);
  };

  props.form.D_Vessel_Name = props.ship ? props.ship.name : "";
  const lastReport = props.lastArrival?.data;

  return (
    <Box component="form" noValidate autoComplete="off">
      <Box sx={{ pt: 0.5, px: 2 }}>
        <Columns
          sx={{
            display: "flex",
            alignItems: "top",
          }}
        >
          <Column>
            <Typography
              component="h3"
              variant="subtitle1"
              align="center"
              sx={{
                lineHeight: 1,
                fontWeight: "bold",
                color: "#fff",
                fontSize: "1.5rem",
                verticalAlign: "super",
              }}
            >
              {props.title}
            </Typography>
            <Typography
              sx={{
                pt: 1,
                textAlign: "left",
                verticalAlign: "top",
                fontSize: "12px",
                fontFamily: "Public Sans",
                lineHeight: "auto",
                color: "#ffffff",
              }}
            >
              *(Prev. Data)
            </Typography>
          </Column>
          <Column sx={{ width: "60%" }}>
            <Steppr step={0} />
          </Column>
        </Columns>
      </Box>
      <Box sx={{ px: 2 }}>
        <Columns
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Column
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <CommonTextFIeld
              isConfirm={props.isConfirm}
              disabled={props.isPreview}
              id="D_Voy"
              label="Voyage number"
              placeholder=""
              value={props.form.D_Voy}
              onChange={(e: string | null) => {
                onChange("D_Voy", e);
              }}
            />
          </Column>
          <Column
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <CommonTextFIeld
              isConfirm={props.isConfirm}
              disabled={props.isPreview}
              id="D_Last_Voy"
              label="Last voyage number"
              placeholder=""
              value={props.form.D_Last_Voy}
              onChange={(e: string | null) => {
                onChange("D_Last_Voy", e);
              }}
            />
          </Column>
          <LastReportText lastData={String(lastReport?.A_Voy)} sx={{ pt: 2 }} />

          <Column
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <FormControl
              sx={{
                m: 1,
                minWidth: 120,
                "& .Mui-focused": {
                  color: "#20BE89",
                },
                "& .MuiFormLabel-root": {
                  color: "#AAAAAA",
                  border: `1px transparent`,
                },
                "& .MuiFormLabel-filled": {
                  color: "#AAAAAA",
                },
              }}
              size="small"
            >
              <InputLabel id="D_LorB" sx={{ color: "#AAAAAA" }}>
                L or B
              </InputLabel>
              <Select
                labelId="D_LorB"
                id="D_LorB"
                label="Loading condition"
                value={props.form.D_LorB}
                onChange={(e: SelectChangeEvent<string | null>) => {
                  onChange("D_LorB", e.target.value);
                }}
                sx={{
                  width: "18ch",
                  "& .MuiSelect-outlined": {
                    border: `1px solid #464E5A !important`,
                  },
                  "& .MuiSelect-select": {
                    color: "#FFFFFF",
                    border: `1px solid #464E5A`,
                  },
                  "& .MuiSelect-select:hover": {
                    color: "#FFFFFF",
                    border: `1px solid #AAAAAA !important`,
                  },
                  "& .MuiSelect-icon": {
                    color: "#FFFFFF",
                  },
                  "&:hover fieldset": {
                    border: "#FFFFFF",
                  },
                  textAlign: "left",
                }}
              >
                {Object.values(LorB).map((v, index) => {
                  return (
                    <MenuItem value={v} key={index}>
                      {v}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Column>
        </Columns>
        <Stack
          direction="row"
          spacing={2}
          alignItems="end"
          justifyContent={"left"}
        >
          <CommonTextFIeld
            isConfirm={props.isConfirm}
            disabled={props.isPreview}
            id="D_Departure_Port"
            label="Departure Port"
            placeholder=""
            value={props.form.D_Departure_Port}
            onChange={(e) => {
              onChange("D_Departure_Port", e);
            }}
          />
          <CommonTextFIeld
            isConfirm={props.isConfirm}
            disabled={props.isPreview}
            id="D_UNLOC"
            label="UNLOC"
            placeholder=""
            value={props.form.D_UNLOC}
            onChange={(e) => {
              onChange("D_UNLOC", e);
            }}
          />
          <CommonSelect
            id="D_EU_UK_port"
            label="EU/UK"
            value={props.form.D_EU_UK_port || ""}
            onChange={(e) => {
              onChange("D_EU_UK_port", e);
            }}
            selectValues={Object.values(EUorUk)}
            isOptional={true}
          />
          <CommonSelect
            id="D_Port_L_U"
            label="L or U"
            value={props.form.D_Port_L_U}
            onChange={(e) => {
              onChange("D_Port_L_U", e);
            }}
            selectValues={Object.values(LorU)}
          />
        </Stack>
        <Columns
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Column>
            <UtcDateTimePicker
              id={"D_Departure_Date_Time"}
              isConfirm={props.isConfirm}
              width="29ch"
              label={"Departure Date & Time (Local)"}
              value={props.form.D_Departure_Date_Time}
              onChange={onChange}
              onChangeKey={"D_Departure_Date_Time"}
              hasMinDate={true}
            />
          </Column>
          <Column>
            <UtcDateTimePicker
              id={"D_RU_Date_Time"}
              isConfirm={props.isConfirm}
              width="24ch"
              label={"R/U Date & Time (Local)"}
              value={props.form.D_RU_Date_Time}
              onChange={onChange}
              onChangeKey={"D_RU_Date_Time"}
              hasMinDate={true}
            />
            <CustomTooltip text={"R/U: Ring up engine"} />
          </Column>
        </Columns>
        <Columns
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Column sx={{ mt: -1 }}>
            <Typography
              sx={{
                textAlign: "left",
                verticalAlign: "top",
                fontSize: "16px",
                fontFamily: "Public Sans",
                lineHeight: "auto",
                color: "#AAAAAA",
                pb: 0.5,
              }}
            >
              TimeZone
            </Typography>
            <RadioButtun
              id="D_TimeZone_PorN"
              defaultValue={props.defaultTimeZone?.slice(0, 1) || "+"}
              label=""
              label1="＋"
              label2="ー"
              value1="+"
              value2="-"
              onChange={onChange}
            />
          </Column>
          <Column sx={{ pl: -2 }}>
            <Typography
              sx={{
                pb: 2,
                textAlign: "left",
                verticalAlign: "top",
                fontSize: "16px",
                fontFamily: "Public Sans",
                lineHeight: "auto",
                color: "#AAAAAA",
              }}
            ></Typography>
            <Select
              size="small"
              labelId="D_TimeZone_HH"
              id="D_TimeZone_HH"
              label="Hour."
              placeholder="Hour."
              value={props.form.D_TimeZone_HH}
              onChange={(e: SelectChangeEvent<string | null>) => {
                onChange("D_TimeZone_HH", e.target.value);
              }}
              inputProps={{
                "aria-label": `D_TimeZone_HH`,
              }}
              sx={{
                width: "70px",
                "& .MuiSelect-outlined": {
                  color: "#FFFFFF",
                },
                "& .MuiSelect-select": {
                  color: "#FFFFFF",
                  border: `1px solid #464E5A`,
                },
                "& .MuiSelect-select:hover": {
                  color: "#FFFFFF",
                  border: `1px solid #AAAAAA !important`,
                },

                "& .MuiSelect-icon": {
                  color: "transparent",
                },
                "&:hover fieldset": {
                  border: "#464E5A",
                },
                textAlign: "left",
              }}
            >
              {[...Array(15).keys()]
                .map((v) => ("00" + String(v)).slice(-2))
                .map((v, index) => {
                  return (
                    <MenuItem value={v} key={index}>
                      {v}
                    </MenuItem>
                  );
                })}
            </Select>
          </Column>

          <Column sx={{ pl: -15 }}>
            <Typography sx={{ color: "#AAAAAA", pt: 2 }}>：</Typography>
          </Column>
          <Column sx={{ pl: -15 }}>
            <Typography
              sx={{
                pb: 2,
                textAlign: "left",
                verticalAlign: "top",
                fontSize: "16px",
                fontFamily: "Public Sans",
                lineHeight: "auto",
                color: "#AAAAAA",
              }}
            ></Typography>
            <Select
              size="small"
              labelId="D_TimeZone_MM"
              id="D_TimeZone_MM"
              label="Min."
              placeholder="Min."
              value={props.form.D_TimeZone_MM}
              onChange={(e: SelectChangeEvent<string | null>) => {
                onChange("D_TimeZone_MM", e.target.value);
              }}
              inputProps={{
                "aria-label": `D_TimeZone_MM`,
              }}
              sx={{
                width: "70px",
                "& .MuiSelect-outlined": {
                  color: "#FFFFFF",
                },
                "& .MuiSelect-select": {
                  color: "#FFFFFF",
                  border: `1px solid #464E5A`,
                },
                "& .MuiSelect-select:hover": {
                  color: "#FFFFFF",
                  border: `1px solid #AAAAAA !important`,
                },

                "& .MuiSelect-icon": {
                  color: "transparent",
                },
                "&:hover fieldset": {
                  border: "#464E5A",
                },
                textAlign: "left",
              }}
            >
              {["00", "30", "45"].map((v, index) => {
                return (
                  <MenuItem value={v} key={index}>
                    {v}
                  </MenuItem>
                );
              })}
            </Select>
          </Column>
          <LastReportText
            lastData={String(lastReport?.A_Time_Zone)}
            sx={{ pt: 4 }}
          />
        </Columns>
        <Box>
          <Columns
            sx={{
              display: "flex",
              alignItems: "end",
            }}
          >
            <Column sx={{ mb: -3 }}>
              <Typography
                sx={{
                  textAlign: "left",
                  verticalAlign: "top",
                  fontSize: "14px",
                  fontFamily: "Public Sans",
                  lineHeight: "auto",
                  color: "#AAAAAA",
                  pl: 1,
                  mb: -2,
                }}
              >
                ROB
              </Typography>
              <CustomTooltip
                text={"Retention of Oil on Board"}
                sx={{ mt: -2, ml: 5.5, mb: 0 }}
              />
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_FO"
                label="FO (mt)"
                placeholder=""
                value={props.form.D_ROB_FO}
                unitName={"mt"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_FO", e);
                }}
              />
            </Column>
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_MGO"
                label="MGO (mt)"
                placeholder=""
                value={props.form.D_ROB_MGO}
                unitName={"mt"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_MGO", e);
                }}
              />
            </Column>
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_MECO"
                label="MECO (L)"
                placeholder=""
                value={props.form.D_ROB_MECO}
                unitName={"L"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_MECO", e);
                }}
              />
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_MESO"
                label="MESO (L)"
                placeholder=""
                value={props.form.D_ROB_MESO}
                unitName={"L"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_MESO", e);
                }}
              />
            </Column>
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_GESO"
                label="GESO (L)"
                placeholder=""
                value={props.form.D_ROB_GESO}
                unitName={"L"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_GESO", e);
                }}
              />
            </Column>
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_ROB_FW"
                label="FW (mt)"
                placeholder=""
                value={props.form.D_ROB_FW}
                unitName={"mt"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_ROB_FW", e);
                }}
              />
            </Column>
          </Columns>
          <RobAdditionalForDeparture {...props} />
          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <CommonTextFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_Cargo_Name"
                label="Cargo name"
                placeholder=""
                value={props.form.D_Cargo_Name}
                onChange={(e: string | null) => {
                  onChange("D_Cargo_Name", e);
                }}
              />
            </Column>
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_Cargo_Qtty"
                label="Cargo quantity"
                placeholder=""
                value={props.form.D_Cargo_Qtty}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_Cargo_Qtty", e);
                }}
              />
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "end",
            }}
          >
            <Column>
              <Typography
                sx={{
                  textAlign: "left",
                  verticalAlign: "top",
                  fontSize: "14px",
                  fontFamily: "Public Sans",
                  lineHeight: "auto",
                  color: "#AAAAAA",
                  pl: 1,
                  mb: -2,
                }}
              >
                Draft
              </Typography>
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "end",
            }}
          >
            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_Draft_Fore"
                label="Fore (m)"
                placeholder=""
                value={props.form.D_Draft_Fore}
                unitName={"m"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_Draft_Fore", e);
                }}
              />
            </Column>

            <Column>
              <CommonNumberFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_Draft_Aft"
                label="Aft (m)"
                placeholder=""
                value={props.form.D_Draft_Aft}
                unitName={"m"}
                minValue={0}
                onChange={(e: string | null) => {
                  onChange("D_Draft_Aft", e);
                }}
              />
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <CommonTextFIeld
                isConfirm={props.isConfirm}
                disabled={props.isPreview}
                id="D_Next_Port_Name"
                label="Next port name"
                placeholder=""
                value={props.form.D_Next_Port_Name}
                onChange={(e: string | null) => {
                  onChange("D_Next_Port_Name", e);
                }}
              />
            </Column>
          </Columns>

          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <UtcDateTimePicker
                id={"D_ETA"}
                isConfirm={props.isConfirm}
                label={"ETA"}
                value={props.form.D_ETA}
                onChange={onChange}
                onChangeKey={"D_ETA"}
                hasMinDate={true}
              />
              <CustomTooltip text={"Estimated Time of Arrival"} />
            </Column>

            <Column>
              <UtcDateTimePicker
                id={"D_ETB"}
                isConfirm={props.isConfirm}
                label={"ETB"}
                value={props.form.D_ETB}
                onChange={onChange}
                onChangeKey={"D_ETB"}
                hasMinDate={true}
              />
              <CustomTooltip text={"Estimated Time of Berthing"} />
            </Column>
          </Columns>
          <Columns
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Column>
              <UtcDateTimePicker
                id={"D_ETC"}
                isConfirm={props.isConfirm}
                label={"ETC"}
                value={props.form.D_ETC}
                onChange={onChange}
                onChangeKey={"D_ETC"}
                hasMinDate={true}
              />
              <CustomTooltip text={"Estimated Time of Completion"} />
            </Column>
            <Column>
              <UtcDateTimePicker
                id={"D_ETD"}
                isConfirm={props.isConfirm}
                label={"ETD"}
                value={props.form.D_ETD}
                onChange={onChange}
                onChangeKey={"D_ETD"}
                hasMinDate={true}
              />
              <CustomTooltip text={"Estimated Time of Departure"} />
            </Column>
          </Columns>

          <Columns
            sx={{
              pl: 3,
              display: "flex",
              alignItems: "end",
            }}
          >
            <TextField
              disabled={props.isPreview}
              sx={{
                margin: "left",
                width: "1000px",
                pt: 4,
                "& .MuiInputBase-input": { color: "#FFFFFF" },
                "& label.Mui-focused": {
                  color: "#20BE89",
                },
                "& label": {
                  mt: 4,
                  color: "#AAAAAA",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#464E5A",
                  },
                  "&:hover fieldset": {
                    borderColor: "#AAAAAA",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#20BE89",
                  },
                },
                textAlign: "left",
              }}
              rows={6}
              id="D_Remark"
              label="Remark"
              placeholder=""
              multiline
              value={props.form.D_Remark || ""}
              inputProps={{
                maxLength: 2000,
              }}
              onChange={(e) => {
                onChange("D_Remark", e.target.value);
              }}
            />
          </Columns>
          <FormBlock_Spare {...props} />
        </Box>
      </Box>
    </Box>
  );
};

export default FormBlock;
